// Lightweight fetch guard to reduce noisy 404s to Supabase REST endpoints
// When the `user_game_stats` table is absent in a Supabase project, many
// clients repeatedly GET/UPSERT that resource causing noisy logs. This file
// intercepts fetch calls to that specific REST path and, after seeing a 404
// once, caches a local skip flag and returns an empty array for subsequent
// calls to keep the UI quiet in development.

const SKIP_KEYS: Record<string, string> = {
  user_game_stats: 'cges:skip_user_game_stats',
  player_progress: 'cges:skip_player_progress',
  player_stats: 'cges:skip_player_stats',
};

const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || '').replace(/\/$/, '');

const FALLBACK_WAITLIST_API = (() => {
  const raw = (import.meta.env.VITE_WAITLIST_API_URL || import.meta.env.VITE_API_URL || '').trim();
  const candidate = raw || 'https://waitlist-api-ue77.onrender.com';
  // Only accept absolute http(s) URLs here.
  if (/^https?:\/\//i.test(candidate)) return candidate.replace(/\/$/, '');
  return 'https://waitlist-api-ue77.onrender.com';
})();

function isSameOriginApiPath(url: string) {
  try {
    const u = new URL(url, window.location.href);
    return u.origin === window.location.origin && u.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

async function looksLikeHtml404(resp: Response) {
  try {
    if (!resp || resp.status !== 404) return false;
    const ct = (resp.headers.get('content-type') || '').toLowerCase();
    if (ct.includes('text/html')) return true;
    // Some hosts return missing content-type; sniff small body.
    const txt = await resp.clone().text().catch(() => '');
    return /<html|<!doctype/i.test(txt);
  } catch {
    return false;
  }
}

async function cacheApiBase(base: string) {
  try {
    const mod = await import('@/lib/runtimeApiDiscovery');
    if (mod && typeof mod.setCachedApiBase === 'function') {
      mod.setCachedApiBase(base);
    }
  } catch {
    // ignore
  }
}

function matchesSupabaseTable(url: string, table: string) {
  if (!supabaseUrl) return false;
  try {
    const u = new URL(url, window.location.href).toString();
    return u.startsWith(supabaseUrl) && u.includes(`/rest/v1/${table}`);
  } catch {
    return false;
  }
}

if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo, init?: RequestInit) => {
    try {
      const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input);

      // Short-circuit known Supabase REST table endpoints if we've cached a
      // skip flag for them to avoid repeated noisy errors.
      for (const tbl of Object.keys(SKIP_KEYS)) {
        if (matchesSupabaseTable(url, tbl)) {
          try {
            if (localStorage.getItem(SKIP_KEYS[tbl]) === '1') {
              return new Response(JSON.stringify([]), { status: 200, headers: { 'Content-Type': 'application/json' } });
            }
          } catch {
            // ignore localStorage errors
          }
        }
      }

      const resp = await originalFetch(input, init);

      // Hostinger (shared hosting) cannot reverse-proxy /api to a separate backend.
      // If we detect a same-origin /api/* request returning an HTML 404 page,
      // transparently retry against the Render backend so FAF keeps working.
      try {
        if (isSameOriginApiPath(url) && (await looksLikeHtml404(resp))) {
          const target = new URL(url, window.location.href);
          const fallback = `${FALLBACK_WAITLIST_API}${target.pathname}${target.search || ''}`;
          // Cache for subsequent requests.
          cacheApiBase(FALLBACK_WAITLIST_API).catch(() => {});
          return await originalFetch(fallback, init);
        }
      } catch {
        // ignore fallback failures
      }

      try {
        // Inspect responses for known Supabase table endpoints. If the server
        // returns an error indicating a missing table or ambiguous column, cache
        // a skip flag and return an empty array to keep the UI quiet.
        for (const tbl of Object.keys(SKIP_KEYS)) {
          if (matchesSupabaseTable(typeof input === 'string' ? input : (input as Request).url, tbl)) {
            if (resp && (resp.status === 404 || resp.status === 400 || resp.status === 422)) {
              // Clone and inspect body (best-effort)
              try {
                const clone = resp.clone();
                const txt = await clone.text();
                if (!txt) break;
                const lower = txt.toLowerCase();
                if (lower.includes('not found') || lower.includes('column reference') || lower.includes('ambiguous')) {
                  try { localStorage.setItem(SKIP_KEYS[tbl], '1'); } catch {}
                  return new Response(JSON.stringify([]), { status: 200, headers: { 'Content-Type': 'application/json' } });
                }
              } catch {
                // ignore parsing errors
              }
            }
          }
        }
      } catch {
        // ignore
      }

      return resp;
    } catch (e: any) {
      // If the fetch failed due to an abort or network error for a known
      // Supabase table endpoint, treat it as a benign failure and return an
      // empty successful response so the UI stays quiet. Otherwise rethrow
      // the error so callers can handle it as normal.
      try {
        for (const tbl of Object.keys(SKIP_KEYS)) {
          try {
            if (matchesSupabaseTable(typeof input === 'string' ? input : (input as Request).url, tbl)) {
              const msg = e && e.message ? String(e.message).toLowerCase() : '';
              if (msg.includes('aborted') || msg.includes('network') || e?.name === 'AbortError') {
                return new Response(JSON.stringify([]), { status: 200, headers: { 'Content-Type': 'application/json' } });
              }
            }
          } catch {
            // ignore failures when inspecting the input
          }
        }
      } catch {
        // fallthrough
      }

      return Promise.reject(e);
    }
  };
}

export {};
